/**
 * Program 12.1 UX3 — EPC Opportunity workspace (extends UX2 cockpit).
 */
import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Descriptions, Input, Row, Space, Tabs, Tag, Typography, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Opportunity } from '@/services/opportunity.service';
import opportunityService from '@/services/opportunity.service';
import { formatDate, formatMoney, formatPercent } from '@/utils/format';
import {
  mergeEpcContext,
  parseEpcContext,
  weightedPipeline,
  type EpcOpportunityContext,
} from './epcContextTypes';
import { PursuitNextActions } from './PursuitNextActions';
import { buildPursuitViewModel } from './pursuitWorkspaceModel';
import { commercialLifecycleService } from '@/services/commercialLifecycle.service';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { useQuery } from '@tanstack/react-query';
import { pursuitPath } from './resolvePursuitOpportunityId';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

export type EpcOpportunityWorkspaceProps = {
  opportunity: Opportunity;
};

function ownerLabel(opp: Opportunity): string {
  const u = opp.salesOwnerUser;
  if (u) return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—';
  return opp.sales_owner || '—';
}

export const EpcOpportunityWorkspace: React.FC<EpcOpportunityWorkspaceProps> = ({ opportunity }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = opportunity.id;
  const [ctxDraft, setCtxDraft] = useState<EpcOpportunityContext>(() =>
    parseEpcContext((opportunity as { epc_context_json?: unknown }).epc_context_json)
  );

  const ctxQ = useQuery({
    queryKey: ['commercial-lifecycle-context', 'opportunity', id],
    queryFn: () => commercialLifecycleService.getContext('opportunity', id),
    staleTime: 30_000,
  });
  const defsQ = useQuery({
    queryKey: ['commercial-lifecycle-definitions'],
    queryFn: () => commercialLifecycleService.getDefinitions(),
    enabled: Boolean(ctxQ.data?.lifecycle?.definitionCode),
  });
  const prequalQ = useQuery({
    queryKey: ['epc-prequalifications', id],
    queryFn: () => epcCommercialService.listPrequalifications(id),
  });
  const tendersQ = useQuery({
    queryKey: ['epc-tenders', id],
    queryFn: () => epcCommercialService.listTenders(id),
  });

  const vm = useMemo(
    () =>
      buildPursuitViewModel({
        opportunityId: id,
        opportunity,
        context: ctxQ.data ?? null,
        definition: (defsQ.data || []).find(
          (d) => d.code === ctxQ.data?.lifecycle?.definitionCode
        ),
        tenders: tendersQ.data ?? [],
        contracts: [],
        prequals: prequalQ.data ?? [],
      }),
    [id, opportunity, ctxQ.data, defsQ.data, tendersQ.data, prequalQ.data]
  );

  const weighted = weightedPipeline(opportunity.estimated_value, opportunity.probability);

  const saveCtx = useMutation({
    mutationFn: () =>
      opportunityService.update(id, {
        epc_context_json: mergeEpcContext(parseEpcContext(ctxDraft), ctxDraft),
      } as Partial<Opportunity>),
    onSuccess: () => {
      message.success('EPC context saved');
      queryClient.invalidateQueries({ queryKey: ['opportunity', id] });
    },
  });

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Current invitation">
            {vm.invitationKind || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Invitation history">
            {vm.invitationHistory.length ? (
              <Space direction="vertical" size={0}>
                {vm.invitationHistory.map((item) => (
                  <Button key={item.id} type="link" style={{ padding: 0 }} onClick={() => navigate(item.href)}>
                    {item.kind} {item.number}
                    {item.current ? ' (current)' : ''}
                  </Button>
                ))}
              </Space>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Stage">{opportunity.stage}</Descriptions.Item>
          <Descriptions.Item label="Type">{opportunity.opportunity_type || '—'}</Descriptions.Item>
          <Descriptions.Item label="Expected close">
            {opportunity.expected_close_date ? formatDate(opportunity.expected_close_date) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Requirements">
            <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {opportunity.requirements || '—'}
            </Paragraph>
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'stakeholders',
      label: 'Customer & Stakeholders',
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Customer">{opportunity.customer_name}</Descriptions.Item>
            <Descriptions.Item label="End user">
              <Input
                value={ctxDraft.end_user || ''}
                onChange={(e) => setCtxDraft((c) => ({ ...c, end_user: e.target.value }))}
                placeholder="End user organization"
              />
            </Descriptions.Item>
            <Descriptions.Item label="Contact">{opportunity.contact_person || '—'}</Descriptions.Item>
          </Descriptions>
          <Button loading={saveCtx.isPending} onClick={() => saveCtx.mutate()}>
            Save stakeholders
          </Button>
        </Space>
      ),
    },
    {
      key: 'scope',
      label: 'Scope',
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <TextArea
            rows={4}
            value={ctxDraft.scope_summary || ''}
            onChange={(e) => setCtxDraft((c) => ({ ...c, scope_summary: e.target.value }))}
            placeholder="Commercial scope summary"
          />
          <TextArea
            rows={2}
            value={ctxDraft.key_deliverables || ''}
            onChange={(e) => setCtxDraft((c) => ({ ...c, key_deliverables: e.target.value }))}
            placeholder="Key deliverables"
          />
          <Button loading={saveCtx.isPending} onClick={() => saveCtx.mutate()}>
            Save scope
          </Button>
        </Space>
      ),
    },
    {
      key: 'qualification',
      label: 'Qualification',
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Strategic fit">
              <Input
                value={ctxDraft.strategic_fit || ''}
                onChange={(e) => setCtxDraft((c) => ({ ...c, strategic_fit: e.target.value }))}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Relationship">
              <Input
                value={ctxDraft.relationship_strength || ''}
                onChange={(e) => setCtxDraft((c) => ({ ...c, relationship_strength: e.target.value }))}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Competition">
              <Input
                value={ctxDraft.competition_level || opportunity.competitors?.slice(0, 80) || ''}
                onChange={(e) => setCtxDraft((c) => ({ ...c, competition_level: e.target.value }))}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Value confidence">
              <Input
                value={ctxDraft.value_confidence || 'INDICATIVE'}
                onChange={(e) =>
                  setCtxDraft((c) => ({
                    ...c,
                    value_confidence: e.target.value as EpcOpportunityContext['value_confidence'],
                  }))
                }
              />
            </Descriptions.Item>
          </Descriptions>
          <Button loading={saveCtx.isPending} onClick={() => saveCtx.mutate()}>
            Save qualification
          </Button>
        </Space>
      ),
    },
    {
      key: 'competition',
      label: 'Competition',
      children: (
        <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{opportunity.competitors || '—'}</Paragraph>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Title level={4} style={{ marginTop: 0 }}>
          {opportunity.opportunity_name}
        </Title>
        <Text type="secondary">
          {opportunity.customer_name} · Opportunity · EPC
        </Text>
        <Row gutter={[16, 12]} style={{ marginTop: 16 }}>
          <Col xs={12} md={6}>
            <Text type="secondary">Estimated Value</Text>
            <div>{formatMoney(opportunity.estimated_value ?? 0, { currency: opportunity.currency || 'AED' })}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Probability</Text>
            <div>{formatPercent(opportunity.probability, { semantics: 'whole', maximumFractionDigits: 0 })}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Weighted Pipeline</Text>
            <div>
              {weighted != null
                ? formatMoney(weighted, { currency: opportunity.currency || 'AED' })
                : '—'}
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Owner</Text>
            <div>{ownerLabel(opportunity)}</div>
          </Col>
        </Row>
        <Space style={{ marginTop: 16 }}>
          <Button type="primary" onClick={() => navigate(pursuitPath(id))}>
            Open Pursuit Workspace
          </Button>
          <Button onClick={() => navigate(`/epc/commercial/invitations/new?opportunityId=${encodeURIComponent(id)}`)}>
            Start EOI / RFQ / RFT
          </Button>
          <Button onClick={() => navigate('/epc/commercial/prequalifications')}>
            Prequalifications
          </Button>
        </Space>
      </Card>

      <Tabs items={tabItems} />

      {vm.nextActions.length > 0 && (
        <Card title="Next actions" size="small">
          <PursuitNextActions actions={vm.nextActions.filter((a) => !a.blocked).slice(0, 5)} />
        </Card>
      )}
    </Space>
  );
};

export default EpcOpportunityWorkspace;
